import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import PlanoContaEmpresaService, { formatarData, IPlanoContaEmpresa, IPlanoContaHistorico } from '../../services/PlanoContaEmpresaService';

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

// DEVN3-466 — linha do tempo de uma conta do plano de contas da empresa. Cada linha do histórico é a
// conta inteira depois da operação; o "antes → depois" sai da comparação com a linha anterior.

const OPERACOES: Record<IPlanoContaHistorico['operacao'], { rotulo: string; cor: string }> = {
    CRIACAO: { rotulo: 'Criação', cor: 'badge-light-primary' },
    EDICAO: { rotulo: 'Edição', cor: 'badge-light-info' },
    ATIVACAO: { rotulo: 'Ativação', cor: 'badge-light-success' },
    INATIVACAO: { rotulo: 'Inativação', cor: 'badge-light-danger' },
};

const referencial = (h: IPlanoContaHistorico) =>
    h.contaReferencial ? `${h.contaReferencial}${h.eventoNome ? ` – ${h.eventoNome}` : ''}` : `Evento ${h.eventoID}`;

const campos: { rotulo: string; valor: (h: IPlanoContaHistorico) => string }[] = [
    { rotulo: 'Código', valor: h => h.codigo },
    { rotulo: 'Nome', valor: h => h.nome },
    { rotulo: 'Conta referencial', valor: referencial },
    { rotulo: 'Início de vigência', valor: h => formatarData(h.dataInicioVigencia) || '—' },
    { rotulo: 'Situação', valor: h => (h.ativo ? 'Ativa' : 'Inativa') },
];

const formatarDataHora = (valor: string) => {
    const d = new Date(valor);
    return isNaN(d.getTime()) ? valor : d.toLocaleString('pt-BR');
};

function PlanoContaHistoricoModal({ empresaId, conta, onFechar }: { empresaId: number; conta: IPlanoContaEmpresa; onFechar: () => void }) {
    const { t } = useTranslation();
    const [historico, setHistorico] = useState<IPlanoContaHistorico[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        PlanoContaEmpresaService.historico(empresaId, conta.id)
            .then(setHistorico)
            .catch((e: any) => Swal.fire({ text: e?.message || t('Não foi possível carregar o histórico da conta.'), icon: 'error', timer: 5000, showConfirmButton: false }))
            .finally(() => setLoading(false));
    }, [empresaId, conta.id, t]);

    // Mais recente primeiro; a comparação é sempre com a linha anterior no tempo.
    const linhas = historico.map((h, i) => ({ atual: h, anterior: i > 0 ? historico[i - 1] : null })).reverse();

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.4)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onFechar}>
            <div className="card shadow-lg" style={{ width: '640px', maxWidth: '94vw', maxHeight: '85vh', borderRadius: '16px', border: 'none' }} onClick={e => e.stopPropagation()}>
                <div className="card-header d-flex justify-content-between align-items-center py-4 px-5" style={{ background: '#F8FAFC', borderRadius: '16px 16px 0 0' }}>
                    <div>
                        <h3 className="card-title fw-bolder text-dark m-0" style={{ fontSize: '18px' }}>{t('Histórico da conta')}</h3>
                        <div className="text-muted" style={{ fontSize: '12px' }}>{conta.codigo} — {conta.nome}</div>
                    </div>
                    <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={onFechar}>
                        <i className="bi bi-x fs-2"></i>
                    </button>
                </div>
                <div className="card-body p-5" style={{ overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-center text-muted py-5">{t('Carregando...')}</div>
                    ) : linhas.length === 0 ? (
                        <div className="text-center text-muted py-5">{t('Nenhum registro no histórico.')}</div>
                    ) : linhas.map(({ atual, anterior }) => {
                        const op = OPERACOES[atual.operacao] ?? { rotulo: atual.operacao, cor: 'badge-light' };
                        const mudancas = anterior
                            ? campos.filter(c => c.valor(atual) !== c.valor(anterior))
                            : campos;

                        return (
                            <div key={atual.id} className="border-start border-3 ps-4 pb-4 mb-2" style={{ borderColor: '#00B7A1' }}>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className={`badge ${op.cor}`}>{t(op.rotulo)}</span>
                                    <span className="text-dark fw-bold" style={{ fontSize: '13px' }}>{formatarDataHora(atual.data)}</span>
                                    <span className="text-muted" style={{ fontSize: '12px' }}>{atual.usuarioNome || t('Usuário não identificado')}</span>
                                </div>
                                <table className="table table-sm mb-0 mt-2" style={{ fontSize: '12px' }}>
                                    <tbody>
                                        {mudancas.map(c => (
                                            <tr key={c.rotulo}>
                                                <td className="text-muted" style={{ width: '150px' }}>{t(c.rotulo)}</td>
                                                <td>
                                                    {anterior && <><span className="text-decoration-line-through text-muted">{c.valor(anterior)}</span> → </>}
                                                    <span className="text-dark">{c.valor(atual)}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default PlanoContaHistoricoModal;
